// utils/paymentUtils.js
import { collection, doc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { recordStatusChange } from './statusUtils'; // Your existing function

/**
 * Record a payment for a package
 * @param {string} packageId - The ID of the package
 * @param {number} amount - Payment amount
 * @param {string} paymentMethod - Payment method (credit_card, bank_transfer, cash, check)
 * @param {string} paidBy - User ID who made the payment
 * @param {string} reference - Optional reference/invoice number
 * @param {string} notes - Optional payment notes
 */
export const recordPayment = async (packageId, amount, paymentMethod, paidBy, reference = '', notes = '') => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be logged in');

  let previousStatus = null; // Declare this outside the transaction

  try {
    // Use a transaction to ensure data consistency
    await runTransaction(db, async (transaction) => {
      // Get the package document
      const packageRef = doc(db, 'packages', packageId);
      const receiptNumberRef = doc(db, 'payments', 'receiptNumber');
      const packageDoc = await transaction.get(packageRef);
      const receiptNumDoc = await transaction.get(receiptNumberRef);

      if (!packageDoc.exists()) {
        throw new Error('Package not found');
      }

      if (!receiptNumDoc.exists()) {
        throw new Error('Receipt number not found');
      }
      
      const packageData = packageDoc.data();
      previousStatus = packageData.status; // Store the previous status

      const receiptNumber = receiptNumDoc.data().number;

      // Check if package is already paid
      if (packageData.isPaid) {
        throw new Error('Package is already paid');
      }

      // Create payment document
      const paymentRef = doc(collection(db, 'payments'));
      const paymentData = {
        packageId,
        //companyId: packageData.companyId,
        amount: parseFloat(amount),
        paymentMethod,
        paymentDate: serverTimestamp(),
        paidBy: user.uid,
        recordedBy: user.uid,
        receiptNumber: 'REC-' + String(receiptNumber).padStart(6, '0'),
        notes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const now = new Date(); 
      const historyEntry = {
        timestamp: now,
        note: `Payment recorded - ${paymentMethod} - $${parseFloat(amount).toFixed(2)}${reference ? ` - Ref: ${reference}` : ''}`,
        paymentId: paymentRef.id,
        recordedBy: user.uid
      };


      transaction.set(paymentRef, paymentData);
      transaction.update(receiptNumberRef, {
        number: receiptNumber + 1
      });

      // Update package with payment info
      transaction.update(packageRef, {
        isPaid: true,
        paymentId: paymentRef.id,
        status: 'delivered',
        updatedAt: serverTimestamp(),
        history: [...(packageData.history || []), historyEntry],
      });
    });
    
    // Record status change (outside transaction since it's not critical)
    // Now previousStatus is accessible here
    if (previousStatus) {
      await recordStatusChange(packageId, 'paid', previousStatus);
    }
    
    console.log('Payment recorded successfully');
    return true;
  } catch (error) {
    console.error('Error recording payment:', error);
    throw error;
  }
};